import type {
  DeliveryDay,
  DeliveryError,
  DeliveryService,
  ApprovalGroup,
} from '../types/delivery';

interface ReducerAction {
  type: string;
  payload?: any;
}

interface ReducerState {
  uniqueDeliveryDays: DeliveryDay[];
  selectedDate: DeliveryDay | undefined;
  selectedDeliveryService: DeliveryService | undefined;
  filteredServices: DeliveryService[];
  instructions?: string;
  purchaseOrder: string;
  errors: DeliveryError[];
  panelError: boolean;
  charsLeft: number;
  overCharCount: boolean;
  serviceDatesOpen: boolean;
  serviceTimesOpen: boolean;
  dismiss: boolean;
  telephone: string;
  email: string;
  selectedOrderApprovalGroup: ApprovalGroup;
}

const INSTRUCTIONS_MAX_CHARS = 140;

export const initialState = {
  uniqueDeliveryDays: [],
  selectedDate: undefined,
  selectedDeliveryService: undefined,
  filteredServices: [],
  instructions: '',
  purchaseOrder: '',
  errors: [],
  panelError: false,
  charsLeft: INSTRUCTIONS_MAX_CHARS,
  overCharCount: false,
  serviceDatesOpen: false,
  serviceTimesOpen: false,
  dismiss: false,
  telephone: '',
  email: '',
};

export const reducer = (
  state: ReducerState,
  action: ReducerAction,
): ReducerState => {
  switch (action.type) {
    case 'HANDLE_CHANGE_INSTRUCTIONS':
      return {
        ...state,
        instructions: action.payload.instructions,
        charsLeft: action.payload.instructions
          ? INSTRUCTIONS_MAX_CHARS - action.payload.instructions.length
          : INSTRUCTIONS_MAX_CHARS,
        overCharCount:
          action.payload.instructions &&
          action.payload.instructions.length >= INSTRUCTIONS_MAX_CHARS,
      };
    case 'CLEAR_ERRORS':
      return {
        ...state,
        errors: action.payload
          ? state.errors.filter(({ type }) => type !== action.payload)
          : [],
      };
    case 'UPDATE_ERRORS':
      return {
        ...state,
        // Only add an error if it's not already in the array
        ...(!state.errors.some(
          error => error.message === action.payload.message,
        ) && {
          errors: [...state.errors, action.payload],
        }),
        panelError: true,
      };
    case 'SET_SHOW_PANEL_ERROR':
      return {
        ...state,
        panelError: action.payload,
      };
    case 'HANDLE_CHANGE_EMAIL':
      return { ...state, email: action.payload };
    case 'HANDLE_CHANGE_PURCHASE_ORDER':
      return { ...state, purchaseOrder: action.payload };
    case 'HANDLE_CHANGE_TELEPHONE':
      return { ...state, telephone: action.payload };
    case 'SET_SELECTED_DELIVERY_SERVICE':
      return {
        ...state,
        selectedDeliveryService: action.payload,
        ...(Object.keys(action.payload).length === 0 && {
          errors: [
            {
              message: 'Please select a Delivery Date/Time',
              type: 'deliveryDateTime',
            },
          ],
        }),
      };
    case 'SET_FILTERED_SERVICES_BY_DATE':
      return { ...state, ...action.payload };
    case 'CLEAR_FILTERED_SERVICES':
      return {
        ...state,
        selectedDate: undefined,
        selectedDeliveryService: undefined,
        filteredServices: [],
      };
    case 'SET_UNIQUE_DELIVERY_DAYS':
      return {
        ...state,
        uniqueDeliveryDays: action.payload,
      };
    case 'SET_SELECTED_ORDER_APPROVAL_GROUP':
      return { ...state, selectedOrderApprovalGroup: action.payload };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};
